// Client library for AI Agents v2 (WP-10).
// Provides types and wraps backend callables for agents, knowledge sets, routing, and provider keys.

import { call } from './callable';

export const AI_PROVIDERS = ['anthropic', 'openai', 'deepseek', 'gemini'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

export const PROVIDER_MODELS: Record<AiProvider, string[]> = {
  anthropic: [
    'claude-3-5-haiku-20241022',
    'claude-3-5-sonnet-20241022',
    'claude-3-opus-20240229',
  ],
  openai: [
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    'o1-mini',
  ],
  deepseek: [
    'deepseek-chat',
    'deepseek-reasoner',
  ],
  gemini: [
    'gemini-1.5-flash',
    'gemini-1.5-pro',
    'gemini-2.0-flash',
  ],
};

export const DEFAULT_PROVIDER: AiProvider = 'anthropic';
export const DEFAULT_MODEL = 'claude-3-5-haiku-20241022';

export interface AiAgentDoc {
  id: string;
  tenantId?: string;
  name: string;
  slug: string;
  status: 'active' | 'inactive';
  description?: string | null;
  systemPrompt: string;
  knowledgeSetId?: string | null;
  provider: AiProvider;
  model: string;
  temperature: number; // 0.0 - 1.0
  maxTokens?: number;
  createdAt?: number;
  updatedAt?: number;
}

export type RoutingRuleType = 'keyword' | 'label' | 'default';

export interface AiRoutingRule {
  id: string;
  type: RoutingRuleType;
  matchType?: 'exact' | 'contains';
  value?: string; // keyword text or labelId
  agentId: string;
}

export interface AiConfigDoc {
  enabled: boolean;
  defaultAgentId?: string | null;
  enabledAgentIds?: string[];
  routingRules: AiRoutingRule[];
  updatedAt?: number;
}

export const KNOWLEDGE_SOURCE_TYPES = [
  'content',
  'url',
  'file',
  'media',
  'google_sheet',
  'api',
] as const;

export type KnowledgeSourceType = (typeof KNOWLEDGE_SOURCE_TYPES)[number];

export interface KnowledgeSourceConfig {
  text?: string;
  url?: string;
  storagePath?: string;
  fileName?: string;
  mimeType?: string;
  sheetId?: string;
  tab?: string;
  apiId?: string;
}

export interface KnowledgeSource {
  id: string;
  type: KnowledgeSourceType;
  name: string;
  config: KnowledgeSourceConfig;
  status: 'pending' | 'indexing' | 'ready' | 'error';
  chunkCount: number;
  error?: string;
  createdAt: number;
  updatedAt: number;
}

export interface KnowledgeSetDoc {
  id: string;
  tenantId?: string;
  name: string;
  description?: string | null;
  sources?: KnowledgeSource[];
  sourceCount?: number;
  totalChunks?: number;
  lastTrainedAt?: number | null;
  createdAt?: number;
  updatedAt?: number;
}

export interface AiProviderInfo {
  configured: boolean;
  keyMasked: string | null;
}

export interface AiProviderKeysResponse {
  ok: boolean;
  providers: Record<AiProvider, AiProviderInfo>;
}

// ── Callables ────────────────────────────────────────────────────────────────

const _aiAgentSave = call<Partial<AiAgentDoc>, { ok: boolean; id?: string }>('aiAgentSave');
const _aiAgentDelete = call<{ id: string }, { ok: boolean }>('aiAgentDelete');
const _aiAgentList = call<Record<string, never>, { ok: boolean; agents: AiAgentDoc[] }>('aiAgentList');
const _aiAgentGet = call<{ id: string }, { ok: boolean; agent: AiAgentDoc }>('aiAgentGet');

const _aiConfigSave = call<AiConfigDoc, { ok: boolean }>('aiConfigSave');
const _aiConfigGet = call<Record<string, never>, { ok: boolean; config: AiConfigDoc }>('aiConfigGet');

const _knowledgeSetSave = call<{ id?: string; name: string; description?: string }, { ok: boolean; id?: string }>('knowledgeSetSave');
const _knowledgeSetDelete = call<{ id: string }, { ok: boolean }>('knowledgeSetDelete');
const _knowledgeSetList = call<Record<string, never>, { ok: boolean; sets: KnowledgeSetDoc[] }>('knowledgeSetList');
const _knowledgeSetGet = call<{ id: string }, { ok: boolean; set: KnowledgeSetDoc }>('knowledgeSetGet');

const _knowledgeSourceAdd = call<
  { knowledgeSetId: string; type: KnowledgeSourceType; name: string; config: KnowledgeSourceConfig },
  { ok: boolean; sourceId: string; chunkCount: number }
>('knowledgeSourceAdd');

const _knowledgeSourceRemove = call<{ knowledgeSetId: string; sourceId: string }, { ok: boolean }>('knowledgeSourceRemove');
const _knowledgeSourceReindex = call<{ knowledgeSetId: string; sourceId: string }, { ok: boolean; chunkCount: number }>('knowledgeSourceReindex');

const _aiProviderKeysSave = call<
  { anthropicKey?: string; openaiKey?: string; deepseekKey?: string; geminiKey?: string },
  { ok: boolean }
>('aiProviderKeysSave');

const _aiProviderKeysGet = call<Record<string, never>, AiProviderKeysResponse>('aiProviderKeysGet');
const _aiProviderModelsList = call<Record<string, never>, { ok: boolean; models: Record<AiProvider, string[]> }>('aiProviderModelsList');

const _voiceNoteTranscribe = call<{ audioBase64: string; mimeType?: string }, { ok: boolean; text: string; tokensUsed: number }>('voiceNoteTranscribe');

// ── Exported API Functions ───────────────────────────────────────────────────

export async function listAiAgents(): Promise<AiAgentDoc[]> {
  const res = await _aiAgentList({});
  return res.data.agents || [];
}

export async function getAiAgent(id: string): Promise<AiAgentDoc> {
  const res = await _aiAgentGet({ id });
  return res.data.agent;
}

export async function saveAiAgent(agent: Partial<AiAgentDoc>): Promise<{ ok: boolean; id?: string }> {
  const res = await _aiAgentSave(agent);
  return res.data;
}

export async function deleteAiAgent(id: string): Promise<{ ok: boolean }> {
  const res = await _aiAgentDelete({ id });
  return res.data;
}

export async function getAiConfig(): Promise<AiConfigDoc> {
  const res = await _aiConfigGet({});
  return res.data.config;
}

export async function saveAiConfig(config: AiConfigDoc): Promise<{ ok: boolean }> {
  const res = await _aiConfigSave(config);
  return res.data;
}

export async function listKnowledgeSets(): Promise<KnowledgeSetDoc[]> {
  const res = await _knowledgeSetList({});
  return res.data.sets || [];
}

export async function getKnowledgeSet(id: string): Promise<KnowledgeSetDoc> {
  const res = await _knowledgeSetGet({ id });
  return res.data.set;
}

export async function saveKnowledgeSet(input: { id?: string; name: string; description?: string }): Promise<{ ok: boolean; id?: string }> {
  const res = await _knowledgeSetSave(input);
  return res.data;
}

export async function deleteKnowledgeSet(id: string): Promise<{ ok: boolean }> {
  const res = await _knowledgeSetDelete({ id });
  return res.data;
}

export async function addKnowledgeSource(input: {
  knowledgeSetId: string;
  type: KnowledgeSourceType;
  name: string;
  config: KnowledgeSourceConfig;
}): Promise<{ ok: boolean; sourceId: string; chunkCount: number }> {
  const res = await _knowledgeSourceAdd(input);
  return res.data;
}

export async function removeKnowledgeSource(knowledgeSetId: string, sourceId: string): Promise<{ ok: boolean }> {
  const res = await _knowledgeSourceRemove({ knowledgeSetId, sourceId });
  return res.data;
}

export async function reindexKnowledgeSource(knowledgeSetId: string, sourceId: string): Promise<{ ok: boolean; chunkCount: number }> {
  const res = await _knowledgeSourceReindex({ knowledgeSetId, sourceId });
  return res.data;
}

export async function getAiProviderKeys(): Promise<Record<AiProvider, AiProviderInfo>> {
  const res = await _aiProviderKeysGet({});
  return res.data.providers;
}

export async function saveAiProviderKeys(keys: {
  anthropicKey?: string;
  openaiKey?: string;
  deepseekKey?: string;
  geminiKey?: string;
}): Promise<{ ok: boolean }> {
  const res = await _aiProviderKeysSave(keys);
  return res.data;
}

export async function listAiProviderModels(): Promise<Record<AiProvider, string[]>> {
  const res = await _aiProviderModelsList({});
  return res.data.models || PROVIDER_MODELS;
}

export async function transcribeAudio(audioBase64: string, mimeType = 'audio/ogg'): Promise<{ text: string; tokensUsed: number }> {
  const res = await _voiceNoteTranscribe({ audioBase64, mimeType });
  return { text: res.data.text, tokensUsed: res.data.tokensUsed };
}
